/**
 * The words of a note that spell checking reads, and what it needs from a checker. This
 * part is small and loads with the editor; the dictionary itself loads later, in spelling.ts.
 */
export interface SpellChecker {
  /** Whether the dictionary, or the words added to it, has `word`. */
  correct(word: string): boolean;
  /** Up to five spellings `word` was likely meant to be, the likeliest first. */
  suggest(word: string): string[];
  /** Whether `word` is correct only because it was added. */
  added(word: string): boolean;
  add(word: string): void;
  remove(word: string): void;
}

export interface SpellingWord {
  from: number;
  to: number;
  word: string;
}

// Letters, with apostrophes inside (don't, it’s); a hyphen ends a word, as e-mail is e and mail.
const WORD = /[\p{L}\p{M}\p{N}_]+(?:['’][\p{L}\p{M}]+)*/gu;

/**
 * The words in `text` worth checking, at `offset` and on: not a single letter, not an
 * abbreviation in capitals, and nothing with a digit or underscore in it, which is a name
 * or a number rather than a word.
 */
export function spellingWords(text: string, offset = 0): SpellingWord[] {
  const words: SpellingWord[] = [];
  for (const match of text.matchAll(WORD)) {
    const word = match[0];
    if ([...word].length < 2 || /[\p{N}_]/u.test(word)) continue;
    if (word === word.toLocaleUpperCase() && word !== word.toLocaleLowerCase()) continue;
    words.push({ from: offset + match.index, to: offset + match.index + word.length, word });
  }
  return words;
}

// Addresses, emails, and @mentions are names, not words.
const NOT_WORDS = /(?:https?:\/\/|www\.)[^\s<>"'`]+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+|(?<![\w@])@[\p{L}\p{N}._-]+|\[\[[^\]\n]*\]\]/gu;

/** The stretches of `text`, from `offset`, that hold no words to check: web and email addresses, @mentions, and [[links]]. */
export function nonWordRanges(text: string, offset = 0): { from: number; to: number }[] {
  return [...text.matchAll(NOT_WORDS)].map((match) => ({
    from: offset + match.index,
    to: offset + match.index + match[0].length,
  }));
}
